/**
 * @module  experience/hardgoods
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';
import { Injectable } from '@angular/core';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { HARDGOODS_CONSTANTS } from '@experience/shop/hardgoods/constants/browse-hardgoods.constants';
@Injectable()
export class CustomReuseStrategy extends RouteReuseStrategy {
  constructor(private stateStoreUtilService: StateStoreUtilsService) {
    super();
  }
  private customerId: string;
  private handlers: { [key: string]: DetachedRouteHandle } = {};

  /**
   * Decides if the route should be stored
   * @returns {boolean}
   */
  public shouldDetach(): boolean {
    return this.stateStoreUtilService.getNextUrl() === HARDGOODS_CONSTANTS.COMPARE_DEVICES;
  }

  /**
   * Store the information for the route we're destructing
   * @param {ActivatedRouteSnapshot} route
   * @param {DetachedRouteHandle} handle
   */
  public store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    this.handlers[`${this.customerId}. ${route.url.join('/') || route.parent.url.join('/')}`] =
      handle;
  }

  /**
   * Return true if we have a stored route object for the next route
   * @param {ActivatedRouteSnapshot} route
   * @returns {boolean}
   */
  public shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const customerId = this.customerId;
    this.customerId = this.stateStoreUtilService.getCustomerId();
    const url = route.url.join('/') || route.parent.url.join('/');
    // tslint:disable next-line no-any
    if (
      (url === HARDGOODS_CONSTANTS.BROWSE_PHONES || url === HARDGOODS_CONSTANTS.BROWSE_TABLET) &&
      this.handlers[`${customerId}. ${url}`]
    ) {
      return !!this.handlers[`${customerId}. ${url}`];
    }
    delete this.handlers[`${customerId}. ${url}`];
    return false;
  }

  /**
   * If we returned true in shouldAttach(), now return the actual route data for restoration
   * @param {ActivatedRouteSnapshot} route
   * @returns {DetachedRouteHandle}
   */
  public retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle {
    this.customerId = this.stateStoreUtilService.getCustomerId();
    const nextUrl = route.url.join('/') || route.parent.url.join('/');
    this.stateStoreUtilService.setNextUrl(nextUrl);
    if (!route.routeConfig || route.routeConfig.loadChildren) {
      return null;
    }
    return this.handlers[`${this.customerId}. ${nextUrl}`];
  }

  /**
   * Reuse the route if we're going to and from the same route
   * @param {ActivatedRouteSnapshot} future
   * @param {ActivatedRouteSnapshot} curr
   * @returns {boolean}
   */
  public shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }
}
